import { Middleware, isRejectedWithValue } from '@reduxjs/toolkit';
import { logoutThunk } from './authSlice';
import type { AppDispatch } from './store';

interface ErrorResponse {
  status: number;
  data: any;
}

export const authMiddleware: Middleware = (store) => (next) => (action) => {
  // Check if the action is a rejected action with a value
  if (isRejectedWithValue(action)) {
    const error = action.payload;
    // Check if the error has a status property and if it's a 4xx client error
    if (isErrorResponse(error) && error.status >= 400 && error.status < 500) {
      console.log(`${error.status} client error detected, clearing auth state and localStorage`);
      
      // Use the thunk to ensure localStorage is properly cleared
      // Handle the async dispatch properly
      (async () => {
        await (store.dispatch as AppDispatch)(logoutThunk());
        
        // Check if we're in a browser environment and not already on the login page
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          // Navigate to login after logout completes
          window.location.href = '/login';
        }
      })().catch((error) => {
        console.error('Error during logout:', error);
        // Still navigate to login even if logout fails
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      });
    }
  }
  
  return next(action);
};

// Type guard to check if the error is of type ErrorResponse
function isErrorResponse(error: any): error is ErrorResponse {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof error.status === 'number'
  );
}